import React from "react";
import { Check, Star, Shield, Lightning, Buildings } from "@phosphor-icons/react";

const TIERS = [
  {
    name: "Essentials",
    icon: Shield,
    price: "$49",
    unit: "/ user / mo",
    tagline: "Solid IT foundation for small teams (3–15 users)",
    features: [
      "24/7 endpoint monitoring & alerts",
      "Automated OS + app patching",
      "Managed antivirus / EDR",
      "Email security (M365 / Google Workspace)",
      "Cloud backup — workstations (100 GB / user)",
      "Help desk — 4-hour response (Mon–Sat, 8am–8pm)",
      "Monthly health report",
    ],
    cta: "Get Essentials quote",
    badge: null,
    serviceKey: "MSP — Essentials Plan",
  },
  {
    name: "Professional",
    icon: Lightning,
    price: "$79",
    unit: "/ user / mo",
    tagline: "For growing teams that can't afford downtime",
    features: [
      "Everything in Essentials",
      "Help desk — 1-hour response, priority queue",
      "Server monitoring + patching",
      "Cloud backup — workstations + servers (500 GB / user)",
      "Microsoft 365 / Google Workspace administration",
      "Mobile device management (MDM)",
      "Quarterly business reviews",
      "Cybersecurity awareness training",
    ],
    cta: "Get Professional quote",
    badge: "Most Popular",
    serviceKey: "MSP — Professional Plan",
  },
  {
    name: "Enterprise",
    icon: Buildings,
    price: "$129",
    unit: "/ user / mo",
    tagline: "Mission-critical SMBs & multi-location teams",
    features: [
      "Everything in Professional",
      "Help desk — 30-min response, 24/7 emergency",
      "Dedicated vCIO + named technician",
      "On-site visits included (4 per quarter)",
      "Advanced EDR + dark web monitoring",
      "Immutable cloud backup w/ 1-yr retention",
      "Compliance assistance (HIPAA / PCI / SOC2)",
      "99.9% uptime SLA with credits",
    ],
    cta: "Talk to a vCIO",
    badge: null,
    serviceKey: "MSP — Enterprise Plan",
  },
];

const CONTRACT_TERMS = [
  "Month-to-month — flexible, cancel anytime",
  "12-month — save 10% on per-user pricing",
  "24-month — save 18% + locked rate",
];

export default function ManagedServices({ onBook }) {
  return (
    <section
      id="managed-it"
      data-testid="managed-services-section"
      className="bg-zinc-50 py-20 md:py-28 border-y border-zinc-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-sm font-semibold uppercase tracking-widest text-blue-600">
            Managed IT · For Business
          </div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900">
            Stop putting out IT fires.
            <span className="block text-blue-600">Hand them to us.</span>
          </h2>
          <p className="mt-4 text-lg text-zinc-600 leading-relaxed max-w-2xl">
            Predictable monthly pricing per user, no surprise invoices. We proactively monitor,
            patch, back up, and secure your team&rsquo;s devices — so you focus on running the business while we eliminate downtime and security risk.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {TIERS.map((t, i) => {
            const Icon = t.icon;
            const featured = !!t.badge;
            return (
              <article
                key={t.name}
                data-testid={`msp-tier-${i}`}
                className={`relative flex flex-col rounded-2xl p-8 ${
                  featured
                    ? "bg-zinc-950 text-white tracing-beam lg:scale-[1.03] lg:-translate-y-2"
                    : "bg-white border border-zinc-200"
                }`}
              >
                {featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-blue-600 text-white text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full">
                    <Star size={12} weight="fill" /> {t.badge}
                  </div>
                )}

                <div
                  className={`h-12 w-12 rounded-xl grid place-items-center ${
                    featured ? "bg-blue-600/30 text-blue-300" : "bg-blue-600/10 text-blue-600"
                  }`}
                >
                  <Icon size={26} weight="duotone" />
                </div>

                <h3 className={`mt-6 font-heading text-xl font-semibold ${featured ? "text-white" : "text-zinc-900"}`}>
                  {t.name}
                </h3>
                <p className={`mt-1 text-sm ${featured ? "text-zinc-400" : "text-zinc-500"}`}>{t.tagline}</p>

                <div className="mt-6 flex items-baseline gap-1">
                  <span className={`font-heading text-5xl font-extrabold tracking-tighter ${featured ? "text-white" : "text-zinc-900"}`}>
                    {t.price}
                  </span>
                  <span className={`text-sm ${featured ? "text-zinc-400" : "text-zinc-500"}`}>{t.unit}</span>
                </div>

                <ul className="mt-7 space-y-2.5 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-[14.5px]">
                      <Check size={17} weight="bold" className={featured ? "text-blue-400 shrink-0 mt-0.5" : "text-blue-600 shrink-0 mt-0.5"} />
                      <span className={featured ? "text-zinc-200" : "text-zinc-700"}>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => onBook && onBook(t.serviceKey)}
                  data-testid={`msp-cta-${i}`}
                  className={`mt-8 inline-flex items-center justify-center rounded-full px-5 h-11 font-semibold transition-all ${
                    featured
                      ? "bg-blue-600 hover:bg-blue-700 text-white blue-glow"
                      : "bg-zinc-900 hover:bg-zinc-800 text-white"
                  }`}
                >
                  {t.cta}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const el = document.getElementById("contact");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  data-testid={`msp-proposal-${i}`}
                  className={`mt-2.5 inline-flex items-center justify-center rounded-full px-5 h-10 text-sm font-semibold transition-all ${
                    featured
                      ? "bg-white/10 hover:bg-white/20 text-white border border-white/20"
                      : "bg-zinc-100 hover:bg-zinc-200 text-zinc-900 border border-zinc-200"
                  }`}
                >
                  Request Proposal
                </button>
              </article>
            );
          })}
        </div>

        {/* Contract terms */}
        <div className="mt-12 grid md:grid-cols-3 gap-4">
          {CONTRACT_TERMS.map((term, i) => (
            <div
              key={i}
              data-testid={`msp-term-${i}`}
              className="flex items-start gap-3 rounded-xl bg-white border border-zinc-200 p-4 text-sm text-zinc-700"
            >
              <Check size={18} weight="bold" className="text-blue-600 shrink-0 mt-0.5" />
              <span>{term}</span>
            </div>
          ))}
        </div>

        <p className="mt-8 text-center text-xs text-zinc-500">
          Volume discount: 25+ users = 5% off · 50+ users = 10% off · 100+ users = custom pricing.
          Onboarding & assessment is always complimentary.
        </p>
      </div>
    </section>
  );
}
